/**
 * PageHeader — the v2 screen header.
 *
 * Mono eyebrow + Fraunces display title + optional right-aligned action,
 * replacing the legacy `page-header` / `page-title` / `page-subtitle` markup
 * across the train screens. Matches the header of the active-session screen so
 * the whole train flow reads as one surface.
 */
import type { ReactNode } from 'react';

import { FlowLabel } from './FlowCard.js';

interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
}

export function PageHeader({ eyebrow, title, subtitle, action }: PageHeaderProps) {
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 12,
        padding: '6px 0 18px',
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        {eyebrow && <FlowLabel style={{ marginBottom: 6 }}>{eyebrow}</FlowLabel>}
        <h1
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-title-size)',
            lineHeight: 'var(--text-title-line)',
            letterSpacing: 'var(--text-title-tracking)',
            fontWeight: 700,
            color: 'var(--ink-1)',
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            style={{
              margin: '6px 0 0',
              fontSize: 'var(--text-body-sm-size)',
              lineHeight: 'var(--text-body-sm-line)',
              color: 'var(--ink-2)',
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
    </header>
  );
}
